import {
  Cryptocurrency,
  Currency,
  SupportedCurrency,
} from '__generated__/globalTypes';
import { useCurrentUserContext } from 'contexts/currentUser';
import { useAmountWithConversion } from 'hooks/useAmountWithConversion';
import { useMonetaryAmount } from 'hooks/useMonetaryAmount';
import {
  cryptocurrencyLabels,
  cryptocurrencySymbols,
  placeholderAddresses,
} from 'lib/cryptocurrency';
import { RoundingMode, SOL_DECIMAL_PLACES, fromLamport } from 'lib/lamport';
import { ETH_DECIMAL_PLACES, fromWei } from 'lib/wei';

export const useCryptoBalance = (hideSymbol?: boolean) => {
  const { currentUser, currency, cryptocurrency } = useCurrentUserContext();
  const { toMonetaryAmount } = useMonetaryAmount();

  const availableBalances = currentUser?.availableBalances;

  const {
    availableBalance,
    monetaryBalance,
    roundedCryptoValue,
    supportedCurrency,
  } = (() => {
    switch (cryptocurrency) {
      case Cryptocurrency.LAMPORT: {
        const mb = toMonetaryAmount(availableBalances?.lamport);
        return {
          availableBalance: mb.lamport,
          monetaryBalance: mb,
          roundedCryptoValue: fromLamport(
            mb.lamport,
            SOL_DECIMAL_PLACES,
            RoundingMode.ROUND_DOWN
          ),
          supportedCurrency: SupportedCurrency.LAMPORT,
        };
      }
      case Cryptocurrency.WEI:
      default: {
        const mb = toMonetaryAmount(availableBalances?.wei);
        return {
          availableBalance: mb.wei,
          monetaryBalance: mb,
          roundedCryptoValue: fromWei(
            mb.wei,
            ETH_DECIMAL_PLACES,
            RoundingMode.ROUND_DOWN
          ),
          supportedCurrency: SupportedCurrency.WEI,
        };
      }
    }
  })();

  const { main, exponent, cryptoAmount } = useAmountWithConversion({
    monetaryAmount: monetaryBalance,
    hideSymbol,
    useGrouping: false,
  });
  const hideBalance = currentUser?.userSettings?.hideBalance;

  return {
    availableBalance,
    monetaryBalance,
    roundedCryptoValue,
    cryptocurrency,
    supportedCurrency,
    hideBalance,
    main,
    exponent,
    balanceInFiat: currency === Currency.ETH ? exponent : main,
    balanceInCrypto: currency === Currency.ETH ? main : exponent,
    cryptoAmount,
    cryptocurrencySymbol: cryptocurrencySymbols[cryptocurrency],
    cryptocurrencyLabel: cryptocurrencyLabels[cryptocurrency],
    placeholderAddress: placeholderAddresses[cryptocurrency],
  };
};
